import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import {
  LaborActualTimePayload,
  LaborRequestPayload,
  LaborReviewEstimate,
} from '../../models/modal-system.interfaces';
import { ModalDialogData } from '../../services/carrier-orders-grid/grid-modal-dispatcher.service';

interface LaborRequestResponse {
  success?: boolean;
  message?: string;
  code?: string | number;
  data?: any;
  raw?: any;
}

@Component({
  selector: 'app-labor-review-modal',
  templateUrl: './labor-review-modal.component.html',
  styleUrls: ['./labor-review-modal.component.scss'],
})
export class LaborReviewModalComponent implements OnInit, OnDestroy {
  readonly API_URL = '/api/dynamic_grids/labor_request.php';
  readonly hotelNightOptions = [
    { value: 0, label: 'No' },
    { value: 1, label: '1 night' },
    { value: 2, label: '2 nights' },
    { value: 3, label: '3 nights' },
  ];

  review: LaborReviewEstimate | null = null;
  themeMode: 'dark' | 'light' = 'dark';
  requestForm!: FormGroup;
  actualTimeForm!: FormGroup;
  parseError = false;
  submitting = false;
  savingActualTime = false;
  nowMs = Date.now();
  private clockRefreshId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private http: HttpClient,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<LaborReviewModalComponent>,
    private toastr: ToastrService,
    @Inject(MAT_DIALOG_DATA) public data: ModalDialogData,
  ) {}

  ngOnInit(): void {
    this.initializeTheme();
    this.review = this.parseReview(this.data.rowData?.['__labor_estimate']);
    this.parseError = !this.review && !!this.data.rowData?.['__labor_estimate'];
    this.buildForm();
    this.clockRefreshId = setInterval(() => {
      this.nowMs = Date.now();
    }, 30000);
  }

  ngOnDestroy(): void {
    if (this.clockRefreshId) clearInterval(this.clockRefreshId);
  }

  get dialogTitle(): string {
    return this.data.modalConfig.dialog_title ?? this.data.headerName ?? 'Onsite Time Details';
  }

  get stageLabel(): string {
    if (!this.review) return 'No estimate available';
    if (this.review.approved_total_minutes != null || this.review.approved_people != null) {
      return 'Approved';
    }
    if (this.review.requested_total_minutes != null || this.review.requested_people != null) {
      return 'Requested';
    }
    return 'Estimated';
  }

  get totalMinutes(): number | null {
    return this.pickMetric('total');
  }

  get installMinutes(): number | null {
    return this.pickMetric('install');
  }

  get handlingMinutes(): number | null {
    return this.pickMetric('handling');
  }

  get people(): number | null {
    if (!this.review) return null;
    return this.review.approved_people ?? this.review.requested_people ?? this.review.estimated_people ?? null;
  }

  get requestedTotal(): number {
    return this.getNumberControlValue('requested_install_minutes') + this.getNumberControlValue('requested_handling_minutes');
  }

  get isTechnicianOrder(): boolean {
    const installMinutes = this.normalizeNullableNumber(this.review?.estimated_install_minutes) ?? 0;
    const techRequired = this.isTruthy(this.data.rowData?.['v_lading_site_checks.technician_required']);
    const hasAssignment = !!String(this.data.rowData?.['__technician_assignment'] ?? '').trim();
    const hasSavedExtras = (this.normalizeNullableNumber(this.review?.requested_technician_drive_hours) ?? 0) > 0
      || this.normalizeHotelNights(this.review?.requested_technician_hotel_nights) > 0;

    return installMinutes > 0 || techRequired || hasAssignment || hasSavedExtras;
  }

  get isClockedIn(): boolean {
    return !!this.review?.actual_started_at;
  }

  get isClockedOut(): boolean {
    return !!this.review?.actual_completed_at;
  }

  get isClockRunning(): boolean {
    return this.isClockedIn && !this.isClockedOut;
  }

  get actualMinutes(): number | null {
    const startedMs = this.timestampMillis(this.review?.actual_started_at);
    if (startedMs === null) return null;

    const completedMs = this.timestampMillis(this.review?.actual_completed_at) ?? this.nowMs;
    return Math.max(0, completedMs - startedMs) / 60000;
  }

  get allowedMinutes(): number | null {
    if (!this.review) return null;
    return this.normalizeNullableNumber(this.review.approved_total_minutes)
      ?? this.normalizeNullableNumber(this.review.estimated_total_minutes);
  }

  get overMinutes(): number | null {
    if (this.actualMinutes === null || this.allowedMinutes === null) return null;
    return Math.max(0, this.actualMinutes - this.allowedMinutes);
  }

  get wentOver(): boolean {
    return this.overMinutes !== null && this.overMinutes > 0;
  }

  get hasExistingRequest(): boolean {
    if (!this.review) return false;

    return this.review.requested_total_minutes != null
      || this.review.requested_install_minutes != null
      || this.review.requested_handling_minutes != null
      || this.review.requested_people != null
      || this.review.requested_technician_drive_hours != null
      || this.normalizeHotelNights(this.review.requested_technician_hotel_nights) > 0
      || !!this.review.requested_notes
      || this.isRequestPendingStatus(this.review.status);
  }

  get submitLabel(): string {
    if (this.submitting) return this.hasExistingRequest ? 'Updating...' : 'Submitting...';
    return this.hasExistingRequest ? 'Update Request' : 'Submit Request';
  }

  close(): void {
    this.dialogRef.close();
  }

  resetForm(): void {
    this.buildForm();
  }

  coerceRequestedPeople(): void {
    const control = this.requestForm.get('requested_people');
    if (!control) return;

    control.setValue(this.normalizeNullableWholeNumber(control.value), { emitEvent: false });
    control.markAsTouched();
    control.updateValueAndValidity({ emitEvent: false });
  }

  submitRequest(): void {
    if (this.requestForm.invalid || this.submitting) return;

    const raw = this.requestForm.getRawValue();
    const payload: LaborRequestPayload = {
      action: 'request',
      lading_uuid: this.data.lading_uuid,
      requested_install_minutes: this.normalizeNumber(raw.requested_install_minutes),
      requested_handling_minutes: this.normalizeNumber(raw.requested_handling_minutes),
      requested_people: this.normalizeNullableWholeNumber(raw.requested_people),
      requested_technician_drive_hours: this.isTechnicianOrder
        ? this.normalizeNullableNumber(raw.requested_technician_drive_hours)
        : null,
      requested_technician_hotel_nights: this.isTechnicianOrder
        ? this.normalizeHotelNights(raw.requested_technician_hotel_nights)
        : 0,
      requested_notes: this.normalizeNotes(raw.requested_notes),
    };

    this.submitting = true;

    this.http.post<LaborRequestResponse>(this.API_URL, payload).subscribe({
      next: (resp) => {
        this.submitting = false;
        if (!this.isSuccess(resp)) {
          this.toastr.error(this.errorMessageForResponse(resp));
          return;
        }

        this.toastr.success(resp?.message || 'Labor request submitted successfully.');
        this.dialogRef.close({ refreshGrid: true });
      },
      error: () => {
        this.submitting = false;
        this.toastr.error('Network error while submitting labor request.');
      },
    });
  }

  saveActualTime(): void {
    if (this.actualTimeForm.invalid || this.savingActualTime || this.submitting) return;

    const raw = this.actualTimeForm.getRawValue();
    const payload: LaborActualTimePayload = {
      action: 'save_actual_time',
      lading_uuid: this.data.lading_uuid,
      actual_started_at: this.toIsoTimestamp(raw.actual_started_at),
      actual_completed_at: this.toIsoTimestamp(raw.actual_completed_at),
    };

    this.savingActualTime = true;

    this.http.post<LaborRequestResponse>(this.API_URL, payload).subscribe({
      next: (resp) => {
        this.savingActualTime = false;
        if (!this.isSuccess(resp)) {
          this.toastr.error(this.errorMessageForResponse(resp));
          return;
        }

        this.toastr.success(resp?.message || 'Actual onsite times saved.');
        this.dialogRef.close({ refreshGrid: true });
      },
      error: () => {
        this.savingActualTime = false;
        this.toastr.error('Network error while saving actual onsite times.');
      },
    });
  }

  formatMinutes(value: number | null): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const rounded = Math.round(Number(value));
    const hours = Math.floor(rounded / 60);
    const minutes = rounded % 60;

    if (hours <= 0) return `${minutes}m`;
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  }

  formatWholeNumber(value: number | null): string {
    if (value == null || Number.isNaN(Number(value))) return '—';
    return String(Math.max(0, Math.ceil(Number(value))));
  }

  formatHours(value: number | null | undefined): string {
    const hours = this.normalizeNullableNumber(value);
    if (hours === null || hours === 0) return '—';
    return `${hours.toLocaleString('en-US', { maximumFractionDigits: 2 })}h`;
  }

  formatHotelNights(value: number | null | undefined): string {
    const nights = this.normalizeHotelNights(value);
    if (nights === 0) return 'No';
    return `${nights} ${nights === 1 ? 'night' : 'nights'}`;
  }

  formatStatus(value: string | null | undefined): string {
    if (!value) return 'Unknown';
    return value
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (match) => match.toUpperCase());
  }

  formatTimestamp(value: string | null | undefined): string {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  private buildForm(): void {
    this.actualTimeForm = this.fb.group({
      actual_started_at: [this.toDatetimeLocal(this.review?.actual_started_at)],
      actual_completed_at: [this.toDatetimeLocal(this.review?.actual_completed_at)],
    }, { validators: this.actualTimeRangeValidator });

    this.requestForm = this.fb.group({
      requested_install_minutes: [
        this.review?.requested_install_minutes ?? this.review?.estimated_install_minutes ?? 0,
        [Validators.min(0)],
      ],
      requested_handling_minutes: [
        this.review?.requested_handling_minutes ?? this.review?.estimated_handling_minutes ?? 0,
        [Validators.min(0)],
      ],
      requested_people: [
        this.normalizeNullableWholeNumber(this.review?.requested_people ?? this.review?.estimated_people ?? null),
        [Validators.min(0), this.wholeNumberValidator],
      ],
      requested_technician_drive_hours: [
        this.review?.requested_technician_drive_hours ?? null,
        [Validators.min(0)],
      ],
      requested_technician_hotel_nights: [
        this.normalizeHotelNights(this.review?.requested_technician_hotel_nights),
        [Validators.min(0), Validators.max(3), this.wholeNumberValidator],
      ],
      requested_notes: [this.review?.requested_notes ?? ''],
    });
  }

  private pickMetric(type: 'install' | 'handling' | 'total'): number | null {
    if (!this.review) return null;

    if (type === 'install') {
      return this.review.approved_install_minutes
        ?? this.review.requested_install_minutes
        ?? this.review.estimated_install_minutes
        ?? null;
    }

    if (type === 'handling') {
      return this.review.approved_handling_minutes
        ?? this.review.requested_handling_minutes
        ?? this.review.estimated_handling_minutes
        ?? null;
    }

    return this.review.approved_total_minutes
      ?? this.review.requested_total_minutes
      ?? this.review.estimated_total_minutes
      ?? null;
  }

  private getNumberControlValue(controlName: 'requested_install_minutes' | 'requested_handling_minutes'): number {
    return this.normalizeNumber(this.requestForm.get(controlName)?.value);
  }

  private normalizeNumber(value: any): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  private normalizeNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
  }

  private timestampMillis(value: string | null | undefined): number | null {
    if (!value) return null;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toDatetimeLocal(value: string | null | undefined): string {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  }

  private toIsoTimestamp(value: any): string | null {
    if (!value) return null;
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
  }

  private normalizeNullableWholeNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.ceil(parsed) : null;
  }

  private normalizeHotelNights(value: any): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.min(3, Math.max(0, Math.round(parsed)));
  }

  private isTruthy(value: any): boolean {
    if (value === true || value === 1) return true;
    return ['true', 't', 'yes', 'y', '1'].includes(String(value ?? '').trim().toLowerCase());
  }

  private initializeTheme(): void {
    const shellTheme = document.querySelector('.cwo-shell[data-theme]')?.getAttribute('data-theme');
    if (shellTheme === 'dark' || shellTheme === 'light') {
      this.themeMode = shellTheme;
      return;
    }

    const storedTheme = window.localStorage.getItem('carrier-work-orders-theme');
    if (storedTheme === 'dark' || storedTheme === 'light') {
      this.themeMode = storedTheme;
      return;
    }

    const menuTheme = window.localStorage.getItem('theme');
    if (menuTheme === 'ams-content-dark' || menuTheme === 'ams-content-light') {
      this.themeMode = menuTheme === 'ams-content-dark' ? 'dark' : 'light';
      return;
    }

    this.themeMode = window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }

  private normalizeNotes(value: any): string | null {
    const notes = String(value ?? '').trim();
    return notes ? notes : null;
  }

  private wholeNumberValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') return null;

    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return { wholeNumber: true };
    }

    return Number.isInteger(parsed) ? null : { wholeNumber: true };
  };

  private actualTimeRangeValidator = (control: AbstractControl): ValidationErrors | null => {
    const startedValue = control.get('actual_started_at')?.value;
    const completedValue = control.get('actual_completed_at')?.value;

    if (completedValue && !startedValue) return { startRequired: true };
    if (!startedValue || !completedValue) return null;

    const startedMs = new Date(startedValue).getTime();
    const completedMs = new Date(completedValue).getTime();
    if (!Number.isFinite(startedMs) || !Number.isFinite(completedMs)) return { invalidActualTime: true };

    return completedMs < startedMs ? { completedBeforeStarted: true } : null;
  };

  private isSuccess(resp: LaborRequestResponse | null | undefined): boolean {
    if (!resp) return false;
    return resp.success === true || resp.code === 200 || resp.code === '200';
  }

  private isRequestPendingStatus(status: string | null | undefined): boolean {
    return ['pending', 'requested'].includes(String(status ?? '').toLowerCase());
  }

  private errorMessageForResponse(resp: LaborRequestResponse | null | undefined): string {
    if (!resp) return 'Failed to submit labor request.';

    const dbError = resp.data?.db_error || resp.raw?.data?.db_error;
    return dbError ? `${resp.message || 'Failed to submit labor request.'}: ${dbError}` : (resp.message || 'Failed to submit labor request.');
  }

  private parseReview(raw: unknown): LaborReviewEstimate | null {
    if (!raw) return null;
    if (typeof raw === 'object') return raw as LaborReviewEstimate;
    if (typeof raw !== 'string') return null;

    try {
      return JSON.parse(raw) as LaborReviewEstimate;
    } catch {
      return null;
    }
  }
}
